import { useEffect, useMemo, useState } from "react";
import { Sparkles, ShieldAlert, Save, RefreshCw } from "lucide-react";
import api from "../../lib/api";

const ACTION_TYPES = [
  "all", "send_message", "create_group", "add_member",
  "mark_status", "issue_api_key", "send_reminder",
];
const LEVELS = ["propose", "autonomous", "forbidden"];

const LEVEL_COPY = {
  propose: "Drafts every action for human approval.",
  autonomous: "Acts directly. Audit-logged. Forbidden actions still gated.",
  forbidden: "Nerea cannot perform this action under any circumstance.",
};

export default function AdminNereaAutonomy() {
  const [rows, setRows] = useState([]);
  const [forbidden, setForbidden] = useState([]);
  const [draft, setDraft] = useState({ action_type: "all", level: "propose" });
  const [saving, setSaving] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/nerea/autonomy");
        if (!cancelled) {
          setRows(data.levels || []);
          setForbidden(data.forbidden_from_autonomous || []);
        }
      } catch { /* leave empty */ }
      try {
        const { data } = await api.get("/admin/nerea/kb/stats");
        if (!cancelled) setStats(data);
      } catch { /* */ }
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const globalRows = useMemo(() => rows.filter((r) => !r.merchant_id), [rows]);
  const merchantRows = useMemo(() => rows.filter((r) => !!r.merchant_id), [rows]);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/nerea/autonomy", draft);
      setReloadTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <Sparkles className="h-3 w-3 text-[#00E5FF]" /> Nerea
      </div>
      <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
        Autonomy levels
      </h1>
      <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed max-w-2xl">
        Decide whether Nerea proposes actions for human approval or acts directly. Defaults
        apply to every merchant; per-merchant overrides take precedence.
      </p>

      {/* Engine + KB at-a-glance */}
      {stats && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="autonomy-stats-row">
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Engine</div>
            <div className="mt-1 font-display text-base text-white truncate">{stats.engine}</div>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">KB chunks</div>
            <div className="mt-1 font-display text-base text-white">
              {(stats.total_current_chunks || 0).toLocaleString()}
            </div>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Sources</div>
            <div className="mt-1 font-display text-base text-white">
              {Object.keys(stats.by_source || {}).join(" · ") || "—"}
            </div>
          </div>
        </div>
      )}

      {/* Hard-locked actions */}
      <div className="mt-8 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] p-5">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-4 w-4 text-amber-300 mt-0.5" />
          <div>
            <div className="font-display text-[15px] text-white">Hard-locked from autonomous</div>
            <p className="mt-1 text-[13px] text-zinc-400 leading-relaxed">
              These actions always require explicit human approval, regardless of the level you set below.
            </p>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {forbidden.map((f) => (
                <li
                  key={f}
                  data-testid={`autonomy-forbidden-${f}`}
                  className="text-[11.5px] font-mono px-2 py-0.5 rounded-md bg-amber-400/10 text-amber-200 border border-amber-400/15"
                >
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Set global default */}
      <div className="mt-8 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5">
        <div className="font-display text-[15px] text-white">Set global default</div>
        <p className="mt-1 text-[12.5px] text-zinc-500">
          Saving creates or updates a row scoped to <code className="text-zinc-400">merchant_id = null</code>.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Action type</div>
            <select
              data-testid="autonomy-action-select"
              value={draft.action_type}
              onChange={(e) => setDraft((d) => ({ ...d, action_type: e.target.value }))}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13.5px] text-white focus:outline-none focus:border-[#00E5FF]/40"
            >
              {ACTION_TYPES.map((a) => (
                <option key={a} value={a} className="bg-[#0a0a0a]">{a}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Level</div>
            <select
              data-testid="autonomy-level-select"
              value={draft.level}
              onChange={(e) => setDraft((d) => ({ ...d, level: e.target.value }))}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13.5px] text-white focus:outline-none focus:border-[#00E5FF]/40"
            >
              {LEVELS.map((l) => (
                <option key={l} value={l} className="bg-[#0a0a0a]">{l}</option>
              ))}
            </select>
          </label>
          <div className="flex items-end">
            <button
              data-testid="autonomy-save-btn"
              onClick={onSave}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 border border-[#00E5FF]/30 text-[#00E5FF] px-3 py-2 text-[13px] transition-colors disabled:opacity-40"
            >
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saving ? "Saving…" : "Save default"}
            </button>
          </div>
        </div>
        <p className="mt-3 text-[12px] text-zinc-500">{LEVEL_COPY[draft.level]}</p>
      </div>

      {/* Existing rows */}
      <div className="mt-8">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">— Global defaults</div>
        <RowTable rows={globalRows} testIdPrefix="autonomy-global" />

        <div className="mt-8 text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">— Per-merchant overrides</div>
        <RowTable rows={merchantRows} testIdPrefix="autonomy-merchant" showMerchant />
      </div>
    </div>
  );
}

function RowTable({ rows, testIdPrefix, showMerchant }) {
  if (!rows.length) {
    return (
      <div
        className="rounded-xl border border-dashed border-white/[0.08] p-5 text-[13px] text-zinc-500"
        data-testid={`${testIdPrefix}-empty`}
      >
        No rows yet.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] overflow-hidden" data-testid={`${testIdPrefix}-table`}>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-zinc-500 text-left text-[11px] uppercase tracking-[0.18em]">
            {showMerchant && <th className="px-4 py-2.5">Merchant</th>}
            <th className="px-4 py-2.5">Action</th>
            <th className="px-4 py-2.5">Level</th>
            <th className="px-4 py-2.5">Set by</th>
            <th className="px-4 py-2.5">Set at</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={(r.merchant_id || "global") + r.action_type} className="border-t border-white/[0.05] text-zinc-300">
              {showMerchant && (
                <td className="px-4 py-2.5 font-mono text-[11.5px] text-zinc-400 truncate max-w-[160px]">
                  {r.merchant_id}
                </td>
              )}
              <td className="px-4 py-2.5 font-mono text-[11.5px]">{r.action_type}</td>
              <td className="px-4 py-2.5">
                <span className={`px-2 py-0.5 rounded-md text-[11.5px] border ${
                  r.level === "autonomous"
                    ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/20"
                    : r.level === "forbidden"
                    ? "bg-rose-400/10 text-rose-300 border-rose-400/20"
                    : "bg-white/[0.04] text-zinc-300 border-white/[0.08]"
                }`}>
                  {r.level}
                </span>
              </td>
              <td className="px-4 py-2.5 font-mono text-[11.5px] text-zinc-400 truncate max-w-[180px]">
                {r.set_by || "—"}
              </td>
              <td className="px-4 py-2.5 text-zinc-400">{(r.set_at || "").slice(0, 19)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
